import { cn } from "../lib/utils";

const VENDOR_LOGO = "/favicon.png";

export interface PlatformMetaInfo {
  name: string;
  logoUrl: string | null;
}

// 소스(source slug) → 표시 메타. label 있으면 그 값이 벤더명(위)+매체(아래) 2줄,
// 없으면 유입경로만 표기. DB(admins)에서 내려온다.
export interface SourceMetaInfo {
  label: string | null;
}

export interface LeadPlatformCellProps {
  source: string | null;
  platformId: string | null;
  channel: string | null;
  // platformId(또는 "organic") → {name, logoUrl}. 각 앱의 summaries 로 구성.
  platformMeta: Map<string, PlatformMetaInfo>;
  // source slug → 표시 메타. 각 앱의 /leads/sources 로 구성.
  sourceMeta?: Map<string, SourceMetaInfo>;
  className?: string;
}

// client/admin 공용 — lead 의 플랫폼(유입경로) 셀. 벤더/매체/미매핑 처리를 한 곳에서.
export function LeadPlatformCell({
  source,
  platformId,
  channel,
  platformMeta,
  sourceMeta,
  className,
}: LeadPlatformCellProps) {
  // 소스에 label 있으면 벤더명(위)+매체(아래) 2줄로 표기.
  const src = source ? sourceMeta?.get(source) : undefined;
  const vendorName = src?.label ?? null;

  // 매체 라벨: 매핑된 platform 이름 우선 → 없으면 channel 원문(미매핑 신규매체)
  // → 둘 다 없을 때만 자연유입(organic).
  // platform_id 가 null 이어도 channel 값이 있으면 원문을 그대로 표기(자연유입으로 뭉개지 않음).
  const mapped = platformId ? platformMeta.get(platformId) : undefined;
  const media =
    mapped?.name ??
    (channel?.trim() || platformMeta.get("organic")?.name || null);
  const mediaLogo = mapped?.logoUrl ?? null;

  // vendor 있으면 벤더명+로고(위) / 매체(아래). 없으면 매체만.
  const name = vendorName ?? media ?? "—";
  const logoUrl = vendorName ? VENDOR_LOGO : mediaLogo;
  const sub = vendorName ? media : null;

  return (
    <div className={cn("flex items-center gap-1.5 min-w-0", className)}>
      {logoUrl ? (
        <img
          src={logoUrl}
          alt={name}
          className="w-4 h-4 rounded object-contain shrink-0"
        />
      ) : (
        <span
          className="w-4 h-4 rounded bg-surface-muted text-[8px] text-ink-subtle flex items-center justify-center font-semibold shrink-0"
          aria-hidden
        >
          {name.slice(0, 1)}
        </span>
      )}
      <div className="min-w-0 leading-tight">
        <div className="text-[12px] text-ink-muted truncate">{name}</div>
        {sub && (
          <div className="text-[11px] text-ink-subtle truncate">{sub}</div>
        )}
      </div>
    </div>
  );
}
